import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClient } from '../context/ClientContext';

export default function PasswordRecovery() {
  const navigate = useNavigate();

  const {
    session,
    isPasswordRecovery,
    updatePassword,
  } = useClient();

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const validatePassword = () => {
    if (!password) {
      return 'Please enter a new password.';
    }

    if (password.length < 8) {
      return 'Password must be at least 8 characters.';
    }

    // Match the existing ProfitYO signup requirements.
    if (!/(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])/.test(password)) {
      return 'Use uppercase, lowercase, and a number.';
    }

    if (!confirmation) {
      return 'Please confirm your new password.';
    }

    if (password !== confirmation) {
      return 'The passwords do not match.';
    }

    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSaving) return;

    setError('');

    // A password reset requires a valid recovery
    // session established by Supabase Auth.
    if (!session || !isPasswordRecovery) {
      setError(
        'Your password recovery session is unavailable. Please request a new recovery link.'
      );
      return;
    }

    const validationError = validatePassword();

    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);

    try {
      const result = await updatePassword(password);

      if (!result?.success) {
        setError(
          result?.error ||
          'Unable to update your password. Please request a new recovery link.'
        );

        return;
      }

      // Clear password fields before leaving.
      setPassword('');
      setConfirmation('');

      // Existing App.jsx handles the authenticated
      // workspace and onboarding redirects.
      navigate('/login', { replace: true });

    } catch (err) {
      console.error('Password recovery error:', err);

      setError(
        'Something went wrong while updating your password. Please try again.'
      );

    } finally {
      setIsSaving(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#f8fafc] p-6">
      <section className="w-full max-w-md rounded-3xl border border-slate-100 bg-white p-8 shadow-xl">

        <div className="mb-7">
          <p className="mb-3 text-xs font-black uppercase tracking-widest text-emerald-600">
            ProfitYo Secure Access
          </p>

          <h1 className="mb-2 text-3xl font-black tracking-tight text-slate-900">
            Set a new password
          </h1>

          <p className="text-sm font-medium leading-relaxed text-slate-500">
            Create a new password to secure your ProfitYo account.
          </p>
        </div>

        {!session || !isPasswordRecovery ? (
          <div className="space-y-5">
            <div
              role="alert"
              className="rounded-xl border border-amber-100 bg-amber-50 p-4 text-sm font-medium text-amber-800"
            >
              Your password recovery session is unavailable or has expired.
              Please request a new password reset link.
            </div>

            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="w-full rounded-xl bg-slate-900 px-4 py-3 text-sm font-black text-white transition hover:bg-black"
            >
              Back to Sign In
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* New password */}
            <div>
              <label
                htmlFor="new-password"
                className="block text-xs font-bold text-slate-600"
              >
                New Password
              </label>

              <div className="relative mt-2">
                <input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  minLength={8}
                  maxLength={128}
                  required
                  value={password}
                  disabled={isSaving}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    setError('');
                  }}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 pr-20 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
                  placeholder="Enter new password"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-500 hover:text-emerald-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {/* Confirm password */}
            <div>
              <label
                htmlFor="confirm-new-password"
                className="block text-xs font-bold text-slate-600"
              >
                Confirm New Password
              </label>

              <div className="relative mt-2">
                <input
                  id="confirm-new-password"
                  type={showConfirmation ? 'text' : 'password'}
                  autoComplete="new-password"
                  minLength={8}
                  maxLength={128}
                  required
                  value={confirmation}
                  disabled={isSaving}
                  onChange={(event) => {
                    setConfirmation(event.target.value);
                    setError('');
                  }}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 pr-20 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
                  placeholder="Confirm new password"
                />

                <button
                  type="button"
                  onClick={() => setShowConfirmation(!showConfirmation)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-500 hover:text-emerald-600"
                  aria-label={showConfirmation ? 'Hide confirmation' : 'Show confirmation'}
                >
                  {showConfirmation ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <p className="text-xs font-medium leading-relaxed text-slate-500">
              Use at least 8 characters, including an uppercase letter,
              a lowercase letter, and a number.
            </p>

            {/* Error message */}
            {error && (
              <div
                role="alert"
                className="rounded-xl border border-rose-100 bg-rose-50 p-3 text-sm font-semibold text-rose-700"
              >
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isSaving}
              className="w-full rounded-xl bg-slate-900 px-4 py-3 text-sm font-black text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSaving
                ? 'Updating Password...'
                : 'Update Password'}
            </button>

          </form>
        )}

      </section>
    </main>
  );
}